import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

// Text size and colour come from the surrounding element, so the same markdown
// reads right in a note, a card or a muted summary.
const components = {
  h1: ({ node, ...props }) => <h3 className="mb-2 mt-3 text-base font-semibold text-slate-900" {...props} />,
  h2: ({ node, ...props }) => <h4 className="mb-1.5 mt-3 font-semibold text-slate-900" {...props} />,
  h3: ({ node, ...props }) => <h5 className="mb-1 mt-2 font-semibold text-slate-800" {...props} />,
  h4: ({ node, ...props }) => <h6 className="mb-1 mt-2 font-semibold text-slate-800" {...props} />,
  p: ({ node, ...props }) => <p className="mb-2" {...props} />,
  ul: ({ node, ...props }) => <ul className="mb-2 list-disc space-y-1 pl-5" {...props} />,
  ol: ({ node, ...props }) => <ol className="mb-2 list-decimal space-y-1 pl-5" {...props} />,
  strong: ({ node, ...props }) => <strong className="font-semibold" {...props} />,
  a: ({ node, ...props }) => <a className="text-cyan-700 underline underline-offset-2" target="_blank" rel="noreferrer" {...props} />,
  code: ({ node, ...props }) => <code className="rounded bg-slate-100 px-1 py-0.5 text-[12px] text-slate-800" {...props} />,
  pre: ({ node, ...props }) => <pre className="mb-2 overflow-x-auto rounded-lg bg-slate-50 p-3 text-[12px]" {...props} />,
  blockquote: ({ node, ...props }) => <blockquote className="mb-2 border-l-2 border-slate-300 pl-3 text-slate-600" {...props} />,
  hr: ({ node, ...props }) => <hr className="my-3 border-slate-200" {...props} />,
  table: ({ node, ...props }) => (
    <div className="mb-2 overflow-x-auto">
      <table className="min-w-full border-collapse text-left" {...props} />
    </div>
  ),
  th: ({ node, ...props }) => <th className="border-b border-slate-200 px-2 py-1 font-semibold text-slate-900" {...props} />,
  td: ({ node, ...props }) => <td className="border-b border-slate-100 px-2 py-1 align-top" {...props} />,
};

// One-line places keep their text as written and render only emphasis and code;
// they often sit inside a button or link, so a link keeps just its text.
const INLINE_ELEMENTS = ['strong', 'em', 'del', 'code'];
// "1. Check", "- Draft" or "# 3 press" is a title, not a list, heading or code block.
const keepLineStarts = (text) => text
  .replace(/^[ \t]+/gm, '')
  .replace(/^(#{1,6}|[-+*])(?=\s|$)/gm, '\\$1')
  .replace(/^>/gm, '\\>')
  .replace(/^(\d{1,9})([.)])(?=\s|$)/gm, '$1\\$2');

/**
 * Agent-written text: notes, charters, memory, updates and questions. `inline`
 * is for titles and clamped one-liners inside a line of other text.
 */
export default function Markdown({ children, inline = false, className = inline ? '' : 'text-sm leading-6 text-slate-700' }) {
  const text = String(children || '');
  if (inline) {
    return (
      <span className={className}>
        <ReactMarkdown remarkPlugins={[remarkGfm]} components={components} allowedElements={INLINE_ELEMENTS} unwrapDisallowed>
          {keepLineStarts(text)}
        </ReactMarkdown>
      </span>
    );
  }
  return (
    <div className={`${className} break-words [&>:first-child]:mt-0 [&>:last-child]:mb-0`}>
      <ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>{text}</ReactMarkdown>
    </div>
  );
}
